import { PostgrestError } from "@supabase/supabase-js";
import { Notification } from "providers/NotifierProvider";
import { supabase } from "supabase/client";
import { Database } from "supabase/types";
import logSentryError from "utils/logSentryError";
import { ProfileRow } from "./profile";

type DbFriend = Database["public"]["Tables"]["friends"];
export type FriendRow = DbFriend["Row"];
export type FriendInsert = DbFriend["Insert"];
export type FriendUpdate = DbFriend["Update"];

export interface FriendData
  extends Record<string, unknown>,
    Pick<FriendRow, "id">,
    Pick<ProfileRow, "avatar" | "name" | "tag"> {
  uid: FriendRow["sent_to"] | FriendRow["requested_by"];
}

export interface FriendRequest extends Pick<FriendRow, "id">, Pick<ProfileRow, "avatar" | "name"> {
  uid: number;
  sent_by_uid?: FriendRow["sent_to"];
  sent_to_uid?: FriendRow["requested_by"];
}

interface GetFriendOrRequest extends Pick<FriendRow, "id" | "requested_by" | "sent_to"> {
  requested_by_user: { name: ProfileRow["name"] };
  requested_by_avatar: { avatar: ProfileRow["avatar"] };
  requested_by_tag: { tag: ProfileRow["tag"] };
  sent_to_user: { name: ProfileRow["name"] };
  sent_to_avatar: { avatar: ProfileRow["avatar"] };
  sent_to_tag: { tag: ProfileRow["tag"] };
}

class FriendApi {
  showNotification?: (notification: Notification) => void;

  constructor(showNotification?: (notification: Notification) => void) {
    this.showNotification = showNotification;
  }

  /**
   * Get friend/request
   *
   * @param id
   * @param authUid
   * @returns
   */
  async getFriend(
    id: number,
    authUid: number
  ): Promise<{
    friend: FriendData | null;
    error: PostgrestError | null;
  }> {
    const res = await supabase
      .from("friends")
      .select(
        `
        id, 
        requested_by, 
        requested_by_user:requested_by ( name ), 
        requested_by_avatar:requested_by ( avatar ), 
        requested_by_tag:requested_by ( tag ), 
        sent_to,
        sent_to_user:sent_to ( name ), 
        sent_to_avatar:sent_to ( avatar ),
        sent_to_tag:sent_to ( tag )
      `
      )
      .eq("id", id)
      .single();
    const data = res.data as unknown as GetFriendOrRequest;
    const { error } = res;

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load friend.",
          severity: "error",
        });
      }

      logSentryError(error, "FRIEND_API: getFriend");
      return { error, friend: null };
    }

    let mappedData: FriendData | null = null;
    if (data) {
      if (data.sent_to === authUid) {
        mappedData = {
          avatar: data.requested_by_avatar.avatar,
          id: data.id,
          name: data.requested_by_user.name,
          uid: data.requested_by,
          tag: data.requested_by_tag.tag,
        };
      } else {
        mappedData = {
          avatar: data.sent_to_avatar.avatar,
          id: data.id,
          uid: data.sent_to,
          name: data.sent_to_user.name,
          tag: data.sent_to_tag.tag,
        };
      }
    }

    return { friend: mappedData, error: null };
  }

  /**
   * Get user friends
   *
   * @param authUid
   * @returns
   */
  async getFriends(authUid: number): Promise<{
    friends: FriendData[];
    error: PostgrestError | null;
  }> {
    const res = await supabase
      .from("friends")
      .select(
        `
        id,
        requested_by, 
        requested_by_user:requested_by ( name ), 
        requested_by_avatar:requested_by ( avatar ), 
        requested_by_tag:requested_by ( tag ), 
        sent_to,
        sent_to_user:sent_to ( name ), 
        sent_to_avatar:sent_to ( avatar ), 
        sent_to_tag:sent_to ( tag )
      `
      )
      .eq("confirmed", true)
      .or(`requested_by.eq.${authUid},sent_to.eq.${authUid}`);
    const data = res.data as unknown as GetFriendOrRequest[];
    const { error } = res;

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load friends.",
          severity: "error",
        });
      }

      logSentryError(error, "FRIEND_API: getFriends");
      return { error, friends: [] };
    }

    if (data) {
      const mappedData: FriendData[] = data.map((request) => {
        if (request.sent_to === authUid) {
          return {
            avatar: request.requested_by_avatar.avatar,
            id: request.id,
            name: request.requested_by_user.name,
            uid: request.requested_by,
            tag: request.requested_by_tag.tag,
          };
        }

        return {
          avatar: request.sent_to_avatar.avatar,
          id: request.id,
          uid: request.sent_to,
          name: request.sent_to_user.name,
          tag: request.sent_to_tag.tag,
        };
      });

      return { friends: mappedData, error: null };
    }

    return { friends: [], error: null };
  }

  /**
   * Get user friend request
   *
   * @param authUid
   * @returns
   */
  async getFriendRequest(
    id: number,
    authUid: number
  ): Promise<{
    friendRequest: FriendRequest | null;
    error: PostgrestError | null;
  }> {
    const res = await supabase
      .from("friends")
      .select(
        `
        id,
        requested_by, 
        requested_by_user:requested_by ( name ), 
        requested_by_avatar:requested_by ( avatar ), 
        sent_to,
        sent_to_user:sent_to ( name ), 
        sent_to_avatar:sent_to ( avatar )
      `
      )
      .eq("id", id)
      .single();
    const data = res.data as unknown as GetFriendOrRequest;
    const { error } = res;

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load friend request.",
          severity: "error",
        });
      }

      logSentryError(error, "FRIEND_API: getFriendRequest");
      return { error, friendRequest: null };
    }

    let mappedData: FriendRequest | null = null;
    if (data) {
      if (data.sent_to === authUid) {
        mappedData = {
          avatar: data.requested_by_avatar.avatar,
          id: data.id,
          name: data.requested_by_user.name,
          uid: data.requested_by,
          sent_to_uid: data.sent_to,
        };
      } else {
        mappedData = {
          avatar: data.sent_to_avatar.avatar,
          id: data.id,
          uid: data.sent_to,
          name: data.sent_to_user.name,
          sent_by_uid: data.requested_by,
        };
      }
    }

    return { friendRequest: mappedData, error: null };
  }

  /**
   * Get user friend requests
   *
   * @param authUid
   * @returns
   */
  async getFriendRequests(authUid: number): Promise<{
    friendRequests: FriendRequest[];
    error: PostgrestError | null;
  }> {
    const res = await supabase
      .from("friends")
      .select(
        `
        id,
        requested_by, 
        requested_by_user:requested_by ( name ), 
        requested_by_avatar:requested_by ( avatar ), 
        sent_to,
        sent_to_user:sent_to ( name ), 
        sent_to_avatar:sent_to ( avatar )
      `
      )
      .eq("confirmed", false)
      .or(`requested_by.eq.${authUid},sent_to.eq.${authUid}`);
    const data = res.data as unknown as GetFriendOrRequest[];
    const { error } = res;

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load friend requests.",
          severity: "error",
        });
      }

      logSentryError(error, "FRIEND_API: getFriendRequests");
      return { error, friendRequests: [] };
    }

    if (data) {
      const mappedData: FriendRequest[] = data.map((request) => {
        if (request.sent_to === authUid) {
          return {
            avatar: request.requested_by_avatar.avatar,
            id: request.id,
            name: request.requested_by_user.name,
            uid: request.requested_by,
            sent_to_uid: request.sent_to,
          };
        }

        return {
          avatar: request.sent_to_avatar.avatar,
          id: request.id,
          uid: request.sent_to,
          name: request.sent_to_user.name,
          sent_by_uid: request.requested_by,
        };
      });

      return { friendRequests: mappedData, error: null };
    }

    return { friendRequests: [], error: null };
  }

  /**
   * Send friend request to another user
   *
   * @param requestedBy
   * @param sentTo
   * @returns
   */
  async sendFriendRequest(requestedBy: number, sentTo: number): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.from("friends").insert({
      requested_by: requestedBy,
      sent_to: sentTo,
    });

    if (error) {
      if (this.showNotification) {
        if (error.code === "23505") {
          this.showNotification({
            message: "Friend request already sent.",
            severity: "warning",
          });
        } else {
          this.showNotification({
            message: "Unable to send friend request.",
            severity: "error",
          });
        }
      }

      logSentryError(error, "FRIEND_API: sendFriendRequest");
      return { error };
    }

    if (this.showNotification) {
      this.showNotification({
        message: "Friend request sent.",
        severity: "success",
      });
    }

    return { error: null };
  }

  /**
   * Accept friend request
   *
   * @param requestId
   */
  async acceptFriendRequest(requestId: number): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.from("friends").update({ confirmed: true }).eq("id", requestId);

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to accept friend request",
          severity: "error",
        });

        logSentryError(error, "FRIEND_API: acceptFriendRequest");
        return { error };
      }
    }

    return { error: null };
  }

  /**
   * Cancel/reject friend request or delete a friend
   *
   * @param requestId
   */
  async deleteFriendOrRequest(requestId: number): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.from("friends").delete().eq("id", requestId);

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to delete user/friend request",
          severity: "error",
        });
      }

      logSentryError(error, "FRIEND_API: deleteFriendOrRequest");
      return { error };
    }

    return { error: null };
  }
}

export default FriendApi;
